export type ContourRow = {
  left: number;
  right: number;
  top: number;
  bottom: number;
};

type Point = [number, number];

export type FieldContour = {
  path: string;
  // The top-right corner of the first row, where the field's settings sit.
  corner: { x: number; y: number };
};

const CONTOUR_PAD_X = 4;
const CONTOUR_PAD_Y = 2;
const CONTOUR_RADIUS = 6;

// The first row starts at the field's first box, clear of the name and colon
// before it on its line; it takes the padding of the rows it wraps onto
// only when it starts at their column, with nothing before it.
const padRowsLeft = (rows: ContourRow[]) => {
  const [first, ...wrapped] = rows;
  const startsLine =
    wrapped.length > 0 &&
    first.left <= Math.min(...wrapped.map((row) => row.left));
  return rows.map((row, index) =>
    index === 0 && !startsLine
      ? row
      : { ...row, left: row.left - CONTOUR_PAD_X },
  );
};

// The rects of one wrapped inline element, one row per line it takes:
// rects of the same line are joined, empty ones dropped.
export const toContourRows = (
  rects: ContourRow[],
  origin: { left: number; top: number },
): ContourRow[] =>
  padRowsLeft(
    rects
      .filter((rect) => rect.right > rect.left && rect.bottom > rect.top)
      .map((rect) => ({
        left: rect.left - origin.left,
        right: rect.right - origin.left + CONTOUR_PAD_X,
        top: rect.top - origin.top - CONTOUR_PAD_Y,
        bottom: rect.bottom - origin.top + CONTOUR_PAD_Y,
      }))
      .sort((a, b) => a.top - b.top)
      .reduce<ContourRow[]>((rows, rect) => {
        const last = rows[rows.length - 1];
        if (last && rect.top < (last.top + last.bottom) / 2) {
          return [
            ...rows.slice(0, -1),
            {
              left: Math.min(last.left, rect.left),
              right: Math.max(last.right, rect.right),
              top: Math.min(last.top, rect.top),
              bottom: Math.max(last.bottom, rect.bottom),
            },
          ];
        }
        return [...rows, rect];
      }, []),
  );

const isRedundant = (previous: Point, point: Point, next: Point) =>
  (previous[0] === point[0] && previous[1] === point[1]) ||
  (previous[0] === point[0] && point[0] === next[0]) ||
  (previous[1] === point[1] && point[1] === next[1]);

// Drops repeated points and points in the middle of a straight edge, until
// every point left is a corner.
const keepCorners = (points: Point[]): Point[] => {
  const kept = points.filter(
    (point, index) =>
      !isRedundant(
        points[(index + points.length - 1) % points.length],
        point,
        points[(index + 1) % points.length],
      ),
  );
  return kept.length === points.length || kept.length < 4
    ? kept
    : keepCorners(kept);
};

const distance = (a: Point, b: Point) => Math.hypot(a[0] - b[0], a[1] - b[1]);

const toward = (from: Point, to: Point, length: number): Point => {
  const total = distance(from, to);
  return [
    from[0] + ((to[0] - from[0]) * length) / total,
    from[1] + ((to[1] - from[1]) * length) / total,
  ];
};

const formatPoint = ([x, y]: Point) => `${x.toFixed(1)} ${y.toFixed(1)}`;

// One outline around rows of wrapped text, as a text selection is drawn:
// the rows meet halfway between them, the right edge runs down and the left
// edge back up, and every corner is rounded.
export const buildFieldContour = (rows: ContourRow[]): FieldContour | null => {
  if (!rows.length) {
    return null;
  }
  const touching = rows.map((row, index) => ({
    ...row,
    top: index === 0 ? row.top : (rows[index - 1].bottom + row.top) / 2,
    bottom:
      index === rows.length - 1
        ? row.bottom
        : (row.bottom + rows[index + 1].top) / 2,
  }));
  const rightEdge = touching.flatMap((row): Point[] => [
    [row.right, row.top],
    [row.right, row.bottom],
  ]);
  const leftEdge = [...touching].reverse().flatMap((row): Point[] => [
    [row.left, row.bottom],
    [row.left, row.top],
  ]);
  const corners = keepCorners([...rightEdge, ...leftEdge]);
  const path = corners
    .map((point, index) => {
      const previous = corners[(index + corners.length - 1) % corners.length];
      const next = corners[(index + 1) % corners.length];
      const radius = Math.min(
        CONTOUR_RADIUS,
        distance(previous, point) / 2,
        distance(point, next) / 2,
      );
      const start = toward(point, previous, radius);
      const end = toward(point, next, radius);
      return `${index === 0 ? 'M' : 'L'} ${formatPoint(start)} Q ${formatPoint(point)} ${formatPoint(end)}`;
    })
    .join(' ');
  return {
    path: `${path} Z`,
    corner: { x: touching[0].right, y: touching[0].top },
  };
};
